// client/components/ui/ListContainer.jsx
/**
 * Zentrale List-Container Komponente für WireTrack
 * Eliminiert Redundanz zwischen 13+ Liste-Komponenten
 */

import React, { useMemo, useRef, useState, useCallback, useEffect } from "react";
import { createPortal } from "react-dom";
import { cn } from "../../utils/themeClasses.js";
import AppButton from "./AppButton.jsx";
import { Z_INDEX } from "./zIndex.js";
import StateCard from "./StateCard.jsx";

/**
 * SortItem - dieselbe Menü-Sprache wie SortButton / StatusFilter (menu-item-*)
 */
export function SortItem({
  active,
  label,
  onClick,
  /** wie SortButton: keine „•“-Markierung bei reinen Preset-Optionen */
  fixedPreset = false,
}) {
  const showMarker = active && !fixedPreset;
  return (
    <AppButton
      type="button"
      variant="ghost"
      onClick={onClick}
      role="menuitem"
      className={cn(
        "w-full !justify-start text-left rounded-lg text-sm outline-none min-h-[44px] h-auto font-normal",
        active ? "menu-item-active font-medium" : "menu-item-idle",
      )}
    >
      {label}
      {showMarker ? " •" : ""}
    </AppButton>
  );
}

/**
 * PopoverPortal - Portal für Sort-Menü
 */
export function PopoverPortal({ pos, onClose, children, ariaLabel = "Sortierung" }) {
  const wrapRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose?.();
    const onClick = (e) => {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target)) onClose?.();
    };
    const onRecalc = () => onClose?.();

    document.addEventListener("keydown", onKey);
    document.addEventListener("pointerdown", onClick);
    window.addEventListener("resize", onRecalc, { passive: true });
    window.addEventListener("scroll", onRecalc, { passive: true });

    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onClick);
      window.removeEventListener("resize", onRecalc);
      window.removeEventListener("scroll", onRecalc);
    };
  }, [onClose]);

  const style = {
    position: "fixed",
    top: `${pos.top}px`,
    left: `${pos.left}px`,
    width: `${pos.width}px`,
    zIndex: Z_INDEX.selectPortal,
  };

  return createPortal(
    <div ref={wrapRef} style={style} role="menu" aria-label={ariaLabel}>
      {children}
    </div>,
    document.body
  );
}

/**
 * ListContainer - Zentrale Liste-Komponente
 * 
 * @param {object} props
 * @param {Array} props.items - Liste der Items
 * @param {boolean} props.loading - Loading-State
 * @param {string|null} props.error - Error-Message
 * @param {function} props.onRetry - Retry-Callback
 * @param {function} props.renderItem - Render-Funktion für Items (item, index) => ReactNode
 * @param {string} props.emptyMessage - Nachricht wenn Liste leer
 * @param {ReactNode} props.header - Header-Bereich (Search, Filter, etc.)
 * @param {ReactNode} props.footer - Footer-Bereich
 * @param {string} props.gridCols - Grid-Columns (default: "grid-cols-1 md:grid-cols-2")
 * @param {string} props.gap - Gap zwischen Items (default: "gap-4")
 */
export default function ListContainer({
  items = [],
  loading = false,
  error = null,
  onRetry = null,
  renderItem,
  emptyMessage = "Keine Einträge gefunden.",
  header = null,
  footer = null,
  gridCols = "grid-cols-1 md:grid-cols-2",
  gap = "gap-4",
}) {
  const cardClass = "rounded-2xl border border-default bg-surface";
  
  const renderedItems = useMemo(
    () =>
      items.map((item, index) => {
        const itemKey = item?.id ?? item?.item_id ?? item?.key ?? index;
        return (
          <React.Fragment key={itemKey}>
            {renderItem(item, index)}
          </React.Fragment>
        );
      }),
    [items, renderItem]
  );
  
  // Loading State
  if (loading) {
    return (
      <div className="space-y-4">
        {header}
        <div className={`grid ${gridCols} ${gap}`}>
          <div className={`${cardClass} h-24 animate-pulse`} />
          <div className={`${cardClass} h-24 animate-pulse`} />
          <div className={`${cardClass} h-24 animate-pulse`} />
        </div>
        {footer}
      </div>
    );
  }

  // Error State
  if (error) {
    return (
      <div className="space-y-4">
        {header}
        <StateCard state="error" message={error} onRetry={onRetry} />
        {footer}
      </div>
    );
  }

  // Empty State
  if (items.length === 0) {
    return (
      <div className="space-y-4">
        {header}
        <StateCard state="empty" message={emptyMessage} className="text-center py-10" />
        {footer}
      </div>
    );
  }

  // List State
  return (
    <div className="space-y-4">
      {header}
      <div className={`grid ${gridCols} ${gap}`}>
        {renderedItems}
      </div>
      {footer}
    </div>
  );
}

/**
 * useSortMenu - Hook für Sort-Menü-Logik
 */
export function useSortMenu(defaultSort = { key: "name", dir: "asc" }, sortOptions = {}) {
  const [sort, setSort] = useState(defaultSort);
  const [sortOpen, setSortOpen] = useState(false);
  const btnRef = useRef(null);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0, width: 224 });

  const openSort = useCallback(() => {
    if (!btnRef.current) return;
    const r = btnRef.current.getBoundingClientRect();
    const menuWidth = 224;
    const vw = window.innerWidth || document.documentElement.clientWidth;
    const left = Math.max(8, Math.min(r.right - menuWidth, vw - menuWidth - 8));
    setMenuPos({ top: r.bottom + 8, left, width: menuWidth });
    setSortOpen(true);
  }, []);

  const toggleDir = useCallback(() => {
    setSort((s) => {
      const def = sortOptions[s.key] || { fixed: false };
      if (def.fixed) return s; // Presets nicht toggeln
      return { ...s, dir: s.dir === "asc" ? "desc" : "asc" };
    });
  }, [sortOptions]);

  const setKey = useCallback((key) => {
    setSort((s) => {
      const def = sortOptions[key] || { dir: "asc", fixed: false };
      return { key, dir: def.fixed ? def.dir : (key === s.key ? s.dir : def.dir) };
    });
  }, [sortOptions]);

  return {
    sort,
    setSort,
    sortOpen,
    setSortOpen,
    btnRef,
    menuPos,
    openSort,
    toggleDir,
    setKey,
  };
}

/**
 * useDebouncedSearch - Hook für debounced Search
 */
export function useDebouncedSearch(initialValue = "", delay = 220) {
  const [filter, setFilter] = useState(initialValue);
  const [debounced, setDebounced] = useState(initialValue);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(filter), delay);
    return () => clearTimeout(t);
  }, [filter, delay]);

  return [filter, setFilter, debounced];
}

/**
 * useSessionStorage - Hook für SessionStorage-Persistenz
 */
export function useSessionStorage(key, defaultValue) {
  const [value, setValue] = useState(() => {
    try {
      return sessionStorage.getItem(key) || defaultValue;
    } catch {
      return defaultValue;
    }
  });

  useEffect(() => {
    try {
      if (value !== defaultValue) {
        sessionStorage.setItem(key, value);
      } else {
        sessionStorage.removeItem(key);
      }
    } catch {
      // Ignore
    }
  }, [key, value, defaultValue]);

  return [value, setValue];
}
